define([
    'santa-components',
    'reactDOM',
    'lodash',
    'experiment',
    'componentsPreviewExtensions/commonPreviewExtensions/stylablePreviewExtension/StylableSantaTypes',
    'stylable-santa-flatten'
], function (
    santaComponents,
    ReactDom,
    _,
    experiment,
    StylableSantaTypes,
    stylableSantaFlatten
) {
    'use strict';

    const {definitions} = StylableSantaTypes;
    const disableTransitions = (styleId, css) => `${css}\n/*disable transitions in edit time*/\n.${styleId}__root, .${styleId}__root * { transition: unset !important;}`;
    const STYLABLE_DRIVER_STATE_SET_VALUE = 'true';

    function createQuickChangeCss(quickChange) {
        const rawDecls = Object.entries(quickChange.declarationMap);
        const declsUnset = rawDecls.filter(decl => !decl[1]).map(([key]) => `${key}: unset;\n`).join('');
        const decls = rawDecls.filter(decl => !!decl[1]).map(([key, value]) => `${key}: ${value};\n`).join('');
        return `${quickChange.selector} {
            ${declsUnset}${decls}}
        `;
    }

    const transformCss = (css, allowTransitions, quickChanges, styleId) => {
        let newCss = css;
        if (!allowTransitions) {
            newCss = disableTransitions(styleId, newCss);
        }
        if (quickChanges) {
            newCss += '/*quick changes*/\n';
            quickChanges.forEach(quickChange => {
                newCss += createQuickChangeCss(quickChange);
            });
        }
        return newCss;
    };

    const updateElementState = (compNode, {selector, stateAttribute}, action) => {
        if (selector) {
            const elements = compNode.parentNode.querySelectorAll(selector);
            if (elements && stateAttribute) {
                Array.from(elements).forEach(element => action(element, stateAttribute));
            }
        }
    };

    const removeForceState = (compNode, forceState) => updateElementState(compNode, forceState || {}, (element, stateAttribute) => {
        element.removeAttribute(stateAttribute);
    });

    const applyForceState = (compNode, forceState) => updateElementState(compNode, forceState || {}, (element, stateAttribute) => {
        element.setAttribute(stateAttribute, STYLABLE_DRIVER_STATE_SET_VALUE);
    });

    const shouldUpdateCSSForceState = (forceStateA = {}, forceStateB = {}) =>
        forceStateA.selector !== forceStateB.selector || forceStateA.stateAttribute !== forceStateB.stateAttribute;

    return baseClass => {
        if (!baseClass.stylable) {
            return baseClass;
        }
        const shouldRelayoutStylableComps = experiment.isOpen('bv_shouldRelayoutStylableComps'); //eslint-disable-line

        //this code is covered with santa-integration-test and with wix-ui-santa it
        class StylablePreviewExtension extends baseClass {
            componentDidUpdate(prevProps, prevState, snapshot) {
                if (shouldUpdateCSSForceState(this.props.forceState, prevProps.forceState)) {
                    const node = ReactDom.findDOMNode(this);
                    removeForceState(node, prevProps.forceState);
                    applyForceState(node, this.props.forceState);
                }
                if (super.componentDidUpdate) {
                    super.componentDidUpdate(prevProps, prevState, snapshot);
                }
            }
        }

        const getCompCss = (styleId, {rawCss, getRawCss, componentViewMode, quickChangeOverrides, registerReLayoutPending, compId}) => {
            if (getRawCss) { // TODO: remove "if" once integration is done.
                rawCss = getRawCss(styleId);
            }
            if (shouldRelayoutStylableComps && registerReLayoutPending) {
                registerReLayoutPending(compId);
            }
            return [transformCss(rawCss, componentViewMode === 'preview', quickChangeOverrides, styleId)];
        };
        getCompCss.cssTypes = {
            getRawCss: definitions.getRawCss,
            rawCss: definitions.rawCss,
            componentViewMode: santaComponents.santaTypesDefinitions.RenderFlags.componentViewMode.isRequired,
            quickChangeOverrides: definitions.quickChangeOverrides,
            compId: santaComponents.santaTypesDefinitions.Component.id,
            registerReLayoutPending: santaComponents.santaTypesDefinitions.Layout.registerReLayoutPending
        };

        const getCompFonts = (styleIds, {rawCss, quickChangeOverrides}) => {
            const css = transformCss(rawCss, true, quickChangeOverrides, 'irrelevant');
            const fonts = stylableSantaFlatten.fontStylable.findFontsInCss(css, () => true, true);
            return fonts;
        };
        const fontsTypes = {
            rawCss: definitions.rawCss,
            quickChangeOverrides: definitions.quickChangeOverrides
        };

        getCompFonts.fontsTypes = fontsTypes;

        StylablePreviewExtension.displayName = `StylablePreviewExtension(${baseClass.displayName})`;
        StylablePreviewExtension.getCompCss = getCompCss;
        StylablePreviewExtension.getCompFonts = getCompFonts;
        StylablePreviewExtension.propTypes = _.defaults({
            forceState: definitions.forceState
        }, baseClass.propTypes);

        return StylablePreviewExtension;
    };
});
